import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, NavLink, useNavigate, useParams, Link } from "react-router-dom";
import { api, CATEGORY_STYLES, PRIMARY_CATEGORIES, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { LogOut, LayoutGrid, Users, Plus, ExternalLink, Search, Trash2, Eye, EyeOff, ChevronLeft, Save, Settings, Upload } from "lucide-react";

const STATUS_LABEL = { demo: "Demo", trial: "Trial", active: "Aktif", inactive: "Pasif" };
const STATUS_STYLE = {
  demo: "bg-neutral-100 text-neutral-700",
  trial: "bg-amber-50 text-amber-800",
  active: "bg-emerald-50 text-emerald-800",
  inactive: "bg-neutral-100 text-neutral-500",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => !u ? "" : (u.startsWith("http") ? u : `${BACKEND}${u}`);

function ImageInput({ label, value, onChange, scope, testid, showPreview = true }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/upload?scope=${encodeURIComponent(scope || "misc")}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Görsel yüklendi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">{label}</span>
      <div className="flex gap-2 items-stretch">
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL veya sağdaki butondan yükle"
          data-testid={`${testid}-url`}
          className="flex-1 px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900 bg-white text-sm"
        />
        <label className="cursor-pointer bg-neutral-100 border border-neutral-200 px-4 py-3 text-sm hover:bg-neutral-200 whitespace-nowrap flex items-center gap-2">
          <Upload size={14} /> {busy ? "Yükleniyor…" : "Yükle"}
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" data-testid={`${testid}-file`} />
        </label>
      </div>
      {showPreview && value && (
        <img src={resolveImg(value)} alt="" className="mt-2 h-24 w-auto object-cover border border-neutral-200" />
      )}
    </label>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="theme-dash min-h-screen">
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-[10px] tracking-[0.32em] text-neutral-500" data-testid="admin-brand">{BRAND.wordmark}</div>
            <span className="text-neutral-300">|</span>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Yönetici</div>
            <span className="text-neutral-300">|</span>
            <div className="text-sm text-neutral-700" data-testid="admin-user">{user?.email}</div>
          </div>
          <button data-testid="admin-logout" onClick={doLogout} className="text-sm text-neutral-700 hover:text-black flex items-center gap-1">
            <LogOut size={14} /> Çıkış
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 md:flex gap-8">
        <nav className="md:w-56 md:sticky md:top-24 md:self-start mb-6 md:mb-0">
          <div className="flex md:flex-col gap-1 overflow-x-auto no-scrollbar" data-testid="admin-nav">
            <NavLink end to="/admin" data-testid="admin-nav-overview" className={({ isActive }) => `flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap transition-colors ${isActive ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"}`}>
              <LayoutGrid size={15} /> Genel
            </NavLink>
            <NavLink to="/admin/barbers" data-testid="admin-nav-barbers" className={({ isActive }) => `flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap transition-colors ${isActive ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"}`}>
              <Users size={15} /> Berber Profilleri
            </NavLink>
            <NavLink to="/admin/platform" data-testid="admin-nav-platform" className={({ isActive }) => `flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap transition-colors ${isActive ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"}`}>
              <Settings size={15} /> Platform Ayarları
            </NavLink>
          </div>
        </nav>

        <main className="flex-1 min-w-0">
          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="barbers" element={<BarberList />} />
            <Route path="barbers/new" element={<BarberNew />} />
            <Route path="barbers/:bid/*" element={<BarberEdit />} />
            <Route path="platform" element={<PlatformSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

/* ---------------- Platform Settings ---------------- */
function PlatformSettings() {
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { api.get("/platform").then((r) => setForm(r.data)); }, []);
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);
  if (!form) return <div className="text-neutral-500">Yükleniyor…</div>;
  const set = (k, v) => { setForm({ ...form, [k]: v }); setDirty(true); };
  const save = async () => {
    try {
      const { data } = await api.patch("/admin/platform", form);
      setForm(data); setDirty(false);
      toast.success("Platform ayarları kaydedildi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kaydedilemedi");
    }
  };
  return (
    <div className="max-w-2xl">
      <div className="text-xs uppercase tracking-widest text-neutral-500">Yönetim</div>
      <h1 className="font-editorial text-4xl mt-1 mb-8">Platform Ayarları</h1>
      <div className="space-y-5">
        <TextField label="Platform adı" k="name" form={form} set={set} />
        <TextField label="Wordmark (üst köşe metni)" k="wordmark" form={form} set={set} />
        <TextField label="Tagline (küçük başlık)" k="tagline" form={form} set={set} />
        <TextField label="Anasayfa kısa açıklama" k="short" form={form} set={set} />
        <ImageInput label="Anasayfa hero görseli" value={form.hero_image} onChange={(v) => set("hero_image", v)} scope="platform" testid="platform-hero" />
        <ImageInput label="Favicon (opsiyonel)" value={form.favicon_url} onChange={(v) => set("favicon_url", v)} scope="platform" testid="platform-favicon" />
        <button onClick={save} data-testid="platform-save" className="bg-neutral-900 text-white px-6 py-3 hover:bg-neutral-800 transition-colors flex items-center gap-2">
          <Save size={15} /> Kaydet
        </button>
      </div>
    </div>
  );
}

/* ---------------- Overview ---------------- */
function AdminOverview() {
  const [summary, setSummary] = useState(null);
  const [barbers, setBarbers] = useState([]);
  useEffect(() => {
    api.get("/admin/summary").then((r) => setSummary(r.data));
    api.get("/admin/barbers").then((r) => setBarbers(r.data.slice(0, 5)));
  }, []);
  return (
    <div>
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Platform</div>
          <h1 className="font-editorial text-4xl mt-1">Genel Bakış</h1>
        </div>
        <Link to="/admin/barbers/new" data-testid="admin-new-barber" className="bg-neutral-900 text-white px-5 py-3 hover:bg-neutral-800 transition-colors flex items-center gap-2 text-sm">
          <Plus size={16} /> Yeni Berber Profili
        </Link>
      </div>

      {!summary ? <div className="text-neutral-500">Yükleniyor…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-summary">
            <Metric label="Toplam Profil" value={summary.total} testid="sum-total" />
            <Metric label="Aktif" value={summary.active} testid="sum-active" />
            <Metric label="Trial" value={summary.trial} testid="sum-trial" />
            <Metric label="Pasif" value={summary.inactive} testid="sum-inactive" />
            <Metric label="Demo Profil" value={summary.demo} testid="sum-demo" />
            <Metric label="Toplam Profil Görüntüleme" value={summary.profile_views} testid="sum-views" />
            <Metric label="Toplam WhatsApp Tıklaması" value={summary.whatsapp_clicks} testid="sum-whatsapp" />
          </div>

          <div className="mt-10">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-neutral-500">Son Profiller</div>
              <Link to="/admin/barbers" className="text-xs text-neutral-700 hover:text-black">Tümünü gör →</Link>
            </div>
            <BarberTable barbers={barbers} />
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="bg-white border border-neutral-200 p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="mt-2 font-editorial text-3xl">{value}</div>
    </div>
  );
}

/* ---------------- Barber list ---------------- */
function BarberList() {
  const [barbers, setBarbers] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    api.get(`/admin/barbers?${params}`).then((r) => setBarbers(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div>
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Yönetim</div>
          <h1 className="font-editorial text-4xl mt-1">Berber Profilleri</h1>
        </div>
        <Link to="/admin/barbers/new" data-testid="list-new-barber" className="bg-neutral-900 text-white px-5 py-3 hover:bg-neutral-800 transition-colors flex items-center gap-2 text-sm">
          <Plus size={16} /> Yeni Berber Profili
        </Link>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px] bg-white border border-neutral-200 px-3 py-2">
          <Search size={15} className="text-neutral-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ad, şehir veya URL uzantısı ile ara"
            data-testid="list-search"
            className="flex-1 outline-none text-sm"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="list-status-filter" className="bg-white border border-neutral-200 px-3 py-2 text-sm">
          <option value="">Tüm durumlar</option>
          <option value="demo">Demo</option>
          <option value="trial">Trial</option>
          <option value="active">Aktif</option>
          <option value="inactive">Pasif</option>
        </select>
      </div>

      <BarberTable barbers={barbers} />
    </div>
  );
}

function BarberTable({ barbers }) {
  if (!barbers.length) return <div className="bg-white border border-neutral-200 p-10 text-center text-sm text-neutral-500" data-testid="barber-table-empty">Kayıt yok.</div>;
  return (
    <div className="bg-white border border-neutral-200 overflow-x-auto" data-testid="barber-table">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
          <tr>
            <th className="text-left px-4 py-3">Berber</th>
            <th className="text-left px-4 py-3">Durum</th>
            <th className="text-left px-4 py-3">Şehir</th>
            <th className="text-right px-4 py-3">Görüntüleme</th>
            <th className="text-right px-4 py-3">WhatsApp</th>
            <th className="text-right px-4 py-3">İşlemler</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {barbers.map((b) => (
            <tr key={b.id} data-testid={`row-${b.slug}`}>
              <td className="px-4 py-3">
                <div className="font-medium">{b.name}</div>
                <div className="text-xs text-neutral-500">/{b.slug}</div>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_STYLE[b.status] || "bg-neutral-100 text-neutral-700"}`}>
                  {STATUS_LABEL[b.status] || "—"}
                </span>
              </td>
              <td className="px-4 py-3 text-neutral-600">{b.city || "—"}</td>
              <td className="px-4 py-3 text-right">{b.profile_views ?? 0}</td>
              <td className="px-4 py-3 text-right">{b.whatsapp_clicks ?? 0}</td>
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end gap-3">
                  <a href={`/${b.slug}`} target="_blank" rel="noreferrer" data-testid={`row-view-${b.slug}`} className="text-xs text-neutral-700 hover:text-black flex items-center gap-1">
                    <ExternalLink size={12} /> Önizle
                  </a>
                  <Link to={`/admin/barbers/${b.id}`} data-testid={`row-edit-${b.slug}`} className="text-xs text-neutral-900 underline underline-offset-4">Düzenle</Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- New barber ---------------- */
const emptyBarber = {
  name: "", slug: "", description: "", short_location: "", city: "", district: "",
  address: "", phone: "", whatsapp: "", instagram: "", google_maps_url: "",
  hero_url: "", logo_url: "",
  latitude: "", longitude: "",
  years_experience: "", show_years_experience: false, google_rating: "",
  status: "trial", is_demo: false,
};

function BarberNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyBarber);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        google_rating: form.google_rating ? Number(form.google_rating) : null,
      };
      const { data } = await api.post("/admin/barbers", payload);
      toast.success("Profil oluşturuldu");
      navigate(`/admin/barbers/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Oluşturulamadı");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to="/admin/barbers" className="text-xs text-neutral-500 flex items-center gap-1 mb-6"><ChevronLeft size={12}/> Geri</Link>
      <h1 className="font-editorial text-4xl mb-8">Yeni Berber Profili</h1>
      <form onSubmit={submit} className="space-y-4 max-w-2xl" data-testid="new-barber-form">
        <TextField label="Berber / Studio adı" k="name" form={form} set={set} required />
        <TextField label="URL uzantısı (slug) — domain.com/uzanti" k="slug" form={form} set={set} placeholder="ornek-barber" required />
        <TextField label="Kısa konum (hero altı)" k="short_location" form={form} set={set} placeholder="Karaköy, İstanbul" />
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Şehir" k="city" form={form} set={set} />
          <TextField label="İlçe" k="district" form={form} set={set} />
        </div>
        <TextField label="Açık adres" k="address" form={form} set={set} />
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Telefon" k="phone" form={form} set={set} />
          <TextField label="WhatsApp numarası (uluslararası)" k="whatsapp" form={form} set={set} placeholder="905555555555" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Instagram kullanıcı adı" k="instagram" form={form} set={set} />
          <TextField label="Google Maps yönlendirme URL" k="google_maps_url" form={form} set={set} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Hero görsel URL" k="hero_url" form={form} set={set} />
          <TextField label="Logo görsel URL" k="logo_url" form={form} set={set} />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <TextField label="Yıllık deneyim" k="years_experience" form={form} set={set} type="number" />
          <TextField label="Google puanı (0-5)" k="google_rating" form={form} set={set} type="number" />
          <div>
            <span className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Durum</span>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="new-status" className="w-full px-4 py-3 border border-neutral-200 bg-white">
              <option value="trial">Trial</option>
              <option value="active">Aktif</option>
              <option value="demo">Demo</option>
              <option value="inactive">Pasif</option>
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_demo} onChange={(e) => set("is_demo", e.target.checked)} data-testid="new-is-demo" />
          Demo profil (dış bağlantılar kamuya kapalı)
        </label>
        <button disabled={busy} data-testid="new-submit" className="bg-neutral-900 text-white px-6 py-3 hover:bg-neutral-800 transition-colors">
          {busy ? "Kaydediliyor…" : "Profili oluştur"}
        </button>
      </form>
    </div>
  );
}

function TextField({ label, k, form, set, type = "text", placeholder = "", required }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">{label}</span>
      <input
        type={type}
        value={form[k] ?? ""}
        onChange={(e) => set(k, e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={`field-${k}`}
        className="w-full px-4 py-3 bg-white border border-neutral-200 focus:outline-none focus:border-neutral-900 transition-colors"
      />
    </label>
  );
}

/* ---------------- Edit barber (tabbed) ---------------- */
function BarberEdit() {
  const { bid } = useParams();
  const [data, setData] = useState(null);
  const load = () => api.get(`/admin/barbers/${bid}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid]);
  if (!data) return <div className="text-neutral-500">Yükleniyor…</div>;
  const b = data.barber;
  return (
    <div>
      <Link to="/admin/barbers" className="text-xs text-neutral-500 flex items-center gap-1 mb-4"><ChevronLeft size={12}/> Berber listesi</Link>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Profil düzenleme</div>
          <h1 className="font-editorial text-4xl mt-1" data-testid="edit-name">{b.name}</h1>
          <div className="text-sm text-neutral-500 mt-1">/{b.slug} · <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 ${STATUS_STYLE[b.status] || "bg-neutral-100"}`}>{STATUS_LABEL[b.status] || "—"}</span></div>
        </div>
        <a href={`/${b.slug}`} target="_blank" rel="noreferrer" data-testid="edit-preview" className="border border-neutral-900 text-neutral-900 px-4 py-2 hover:bg-neutral-900 hover:text-white transition-colors flex items-center gap-2 text-sm">
          <ExternalLink size={14} /> Profili Görüntüle
        </a>
      </div>

      <div className="border-b border-neutral-200 mb-6 flex gap-1 overflow-x-auto no-scrollbar" data-testid="edit-tabs">
        {["profil", "portfolyo", "hizmetler", "yorumlar", "qr"].map((t) => (
          <NavLink key={t} end={t === "profil"} to={t === "profil" ? `/admin/barbers/${bid}` : `/admin/barbers/${bid}/${t}`} data-testid={`edit-tab-${t}`} className={({ isActive }) => `px-4 py-3 text-sm capitalize transition-colors ${isActive ? "border-b-2 border-neutral-900 text-neutral-900" : "text-neutral-500 hover:text-neutral-900"}`}>
            {t}
          </NavLink>
        ))}
      </div>

      <Routes>
        <Route index element={<BarberProfileTab barber={b} account={data.account} reload={load} />} />
        <Route path="portfolyo" element={<BarberPortfolioTab bid={bid} haircuts={data.haircuts} reload={load} />} />
        <Route path="hizmetler" element={<BarberServicesTab bid={bid} services={data.services} reload={load} />} />
        <Route path="yorumlar" element={<BarberReviewsTab bid={bid} reviews={data.reviews} reload={load} />} />
        <Route path="qr" element={<BarberQRTab slug={b.slug} />} />
      </Routes>
    </div>
  );
}

function BarberProfileTab({ barber, account, reload }) {
  const [form, setForm] = useState(barber);
  const [accountForm, setAccountForm] = useState({ email: "", password: "" });
  const [accountBusy, setAccountBusy] = useState(false);

  useEffect(() => setForm(barber), [barber]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const createAccount = async (e) => {
    e.preventDefault();

    if (!accountForm.email.trim() || accountForm.password.length < 8) {
      toast.error("Geçerli e-posta ve en az 8 karakterlik şifre gerekli");
      return;
    }

    setAccountBusy(true);

    try {
      await api.post(`/admin/barbers/${barber.id}/account`, {
        email: accountForm.email.trim(),
        password: accountForm.password,
      });

      setAccountForm({ email: "", password: "" });
      toast.success("Giriş hesabı oluşturuldu");
      reload();
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) ||
        "Giriş hesabı oluşturulamadı"
      );
    } finally {
      setAccountBusy(false);
    }
  };
  const setHour = (i, k, v) => {
    const hrs = [...(form.opening_hours || [])];
    hrs[i] = { ...hrs[i], [k]: v };
    setForm({ ...form, opening_hours: hrs });
  };
  const save = async () => {
    const payload = {
      name: form.name, description: form.description, short_location: form.short_location,
      city: form.city, district: form.district, address: form.address,
      phone: form.phone, whatsapp: form.whatsapp, instagram: form.instagram,
      google_maps_url: form.google_maps_url, hero_url: form.hero_url, logo_url: form.logo_url,
      latitude: form.latitude ? Number(form.latitude) : null,
      longitude: form.longitude ? Number(form.longitude) : null,
      years_experience: form.years_experience ? Number(form.years_experience) : null,
      show_years_experience: !!form.show_years_experience,
      google_rating: form.google_rating ? Number(form.google_rating) : null,
      opening_hours: form.opening_hours, slug: form.slug,
      status: form.status, is_demo: !!form.is_demo,
      barber_person_name: form.barber_person_name || "",
      barber_role: form.barber_role || "",
      barber_bio: form.barber_bio || "",
      barber_portrait_url: form.barber_portrait_url || "",
      specialties: Array.isArray(form.specialties) ? form.specialties : (typeof form.specialties === "string" ? form.specialties.split(",").map((s) => s.trim()).filter(Boolean) : []),
    };
    try {
      await api.patch(`/admin/barbers/${barber.id}`, payload);
      toast.success("Kaydedildi");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kaydedilemedi");
    }
  };
  const del = async () => {
    if (!window.confirm(`${barber.name} profili tamamen silinsin mi?`)) return;
    await api.delete(`/admin/barbers/${barber.id}`);
    toast.success("Silindi");
    window.location.href = "/admin/barbers";
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="border border-neutral-200 bg-white p-5">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
          Giriş Hesabı
        </div>

        {barber.is_demo ? (
          <div className="text-sm text-neutral-500">
            Demo profillere giriş hesabı bağlanamaz.
          </div>
        ) : account ? (
          <div>
            <div className="text-sm font-medium text-neutral-900">
              Hesap bağlı
            </div>
            <div className="text-sm text-neutral-500 mt-1">
              {account.email}
            </div>
          </div>
        ) : (
          <form onSubmit={createAccount} className="space-y-3">
            <div className="text-sm text-neutral-600">
              Bu profile giriş yapacak berber hesabını oluştur.
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <input
                type="email"
                value={accountForm.email}
                onChange={(e) =>
                  setAccountForm({ ...accountForm, email: e.target.value })
                }
                placeholder="E-posta"
                autoComplete="off"
                data-testid="barber-account-email"
                className="w-full px-4 py-3 bg-white border border-neutral-200 focus:outline-none focus:border-neutral-900"
              />

              <input
                type="password"
                value={accountForm.password}
                onChange={(e) =>
                  setAccountForm({ ...accountForm, password: e.target.value })
                }
                placeholder="Başlangıç şifresi"
                minLength={8}
                autoComplete="new-password"
                data-testid="barber-account-password"
                className="w-full px-4 py-3 bg-white border border-neutral-200 focus:outline-none focus:border-neutral-900"
              />
            </div>

            <button
              type="submit"
              disabled={accountBusy}
              data-testid="barber-account-create"
              className="bg-neutral-900 text-white px-5 py-2.5 text-sm hover:bg-neutral-800 disabled:opacity-50"
            >
              {accountBusy ? "Oluşturuluyor…" : "Giriş hesabı oluştur"}
            </button>
          </form>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <TextField label="Studio adı" k="name" form={form} set={set} required />
        <TextField label="URL uzantısı (slug)" k="slug" form={form} set={set} required />
      </div>
      <label className="block">
        <span className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Açıklama</span>
        <textarea rows={3} value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} data-testid="edit-description" className="w-full px-4 py-3 bg-white border border-neutral-200 focus:outline-none focus:border-neutral-900" />
      </label>

      <div className="space-y-3 border border-neutral-200 bg-white p-5">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Berberini Tanı</div>
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Barber adı (kişi)" k="barber_person_name" form={form} set={set} placeholder="Emre Kaya" />
          <TextField label="Rol / ünvan" k="barber_role" form={form} set={set} placeholder="Barber / Founder" />
        </div>
        <ImageInput label="Barber portresi" value={form.barber_portrait_url} onChange={(v) => set("barber_portrait_url", v)} scope={`barber-${barber.id}`} testid="edit-portrait" />
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Kısa biyografi (1-3 cümle)</span>
          <textarea rows={3} value={form.barber_bio ?? ""} onChange={(e) => set("barber_bio", e.target.value)} data-testid="edit-bio" className="w-full px-4 py-3 bg-white border border-neutral-200 focus:outline-none focus:border-neutral-900" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Uzmanlıklar (virgülle ayır)</span>
          <input
            type="text"
            value={Array.isArray(form.specialties) ? form.specialties.join(", ") : (form.specialties ?? "")}
            onChange={(e) => set("specialties", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            placeholder="Fade, Taper, Sakal"
            data-testid="edit-specialties"
            className="w-full px-4 py-3 bg-white border border-neutral-200 focus:outline-none focus:border-neutral-900"
          />
        </label>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <TextField label="Kısa konum (hero altı)" k="short_location" form={form} set={set} />
        <div className="grid grid-cols-2 gap-2">
          <TextField label="Şehir" k="city" form={form} set={set} />
          <TextField label="İlçe" k="district" form={form} set={set} />
        </div>
      </div>
      <TextField label="Açık adres" k="address" form={form} set={set} />
      <div className="grid md:grid-cols-2 gap-4">
        <TextField label="Telefon" k="phone" form={form} set={set} />
        <TextField label="WhatsApp" k="whatsapp" form={form} set={set} placeholder="905555555555" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <TextField label="Instagram kullanıcı adı" k="instagram" form={form} set={set} />
        <TextField label="Google Maps URL" k="google_maps_url" form={form} set={set} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <ImageInput label="Hero (kapak) görsel" value={form.hero_url} onChange={(v) => set("hero_url", v)} scope={`barber-${barber.id}`} testid="edit-hero" />
        <ImageInput label="Logo / profil görseli" value={form.logo_url} onChange={(v) => set("logo_url", v)} scope={`barber-${barber.id}`} testid="edit-logo" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <TextField label="Enlem" k="latitude" form={form} set={set} />
        <TextField label="Boylam" k="longitude" form={form} set={set} />
        <TextField label="Google puanı" k="google_rating" form={form} set={set} type="number" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <TextField label="Yıllık deneyim" k="years_experience" form={form} set={set} type="number" />
        <label className="flex items-center gap-2 md:col-span-2 mt-6 text-sm">
          <input type="checkbox" checked={!!form.show_years_experience} onChange={(e) => set("show_years_experience", e.target.checked)} data-testid="edit-show-years" />
          Deneyim yılı profilde gösterilsin
        </label>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <span className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Durum</span>
          <select value={form.status ?? "trial"} onChange={(e) => set("status", e.target.value)} data-testid="edit-status" className="w-full px-4 py-3 border border-neutral-200 bg-white">
            <option value="trial">Trial</option>
            <option value="active">Aktif</option>
            <option value="demo">Demo</option>
            <option value="inactive">Pasif</option>
          </select>
        </div>
        <label className="flex items-center gap-2 mt-6 text-sm">
          <input type="checkbox" checked={!!form.is_demo} onChange={(e) => set("is_demo", e.target.checked)} data-testid="edit-is-demo" />
          Demo profil (dış bağlantılar kapalı)
        </label>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Çalışma saatleri</div>
        <div className="border border-neutral-200 divide-y divide-neutral-200 bg-white">
          {(form.opening_hours || []).map((h, i) => (
            <div key={h.day} className="grid grid-cols-12 items-center gap-2 px-4 py-3">
              <div className="col-span-4 md:col-span-3 text-sm">{h.day}</div>
              <label className="col-span-4 md:col-span-3 flex items-center gap-2 text-xs text-neutral-500">
                <input type="checkbox" checked={h.closed} onChange={(e) => setHour(i, "closed", e.target.checked)} /> Kapalı
              </label>
              <input disabled={h.closed} type="time" value={h.open} onChange={(e) => setHour(i, "open", e.target.value)} className="col-span-2 md:col-span-3 border border-neutral-200 px-2 py-1 text-sm disabled:bg-neutral-50" />
              <input disabled={h.closed} type="time" value={h.close} onChange={(e) => setHour(i, "close", e.target.value)} className="col-span-2 md:col-span-3 border border-neutral-200 px-2 py-1 text-sm disabled:bg-neutral-50" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center gap-4 flex-wrap pt-4">
        <button onClick={save} data-testid="edit-save" className="bg-neutral-900 text-white px-6 py-3 hover:bg-neutral-800 transition-colors flex items-center gap-2">
          <Save size={15} /> Kaydet
        </button>
        <button onClick={del} data-testid="edit-delete" className="text-red-600 text-sm flex items-center gap-1">
          <Trash2 size={13} /> Profili sil
        </button>
      </div>
    </div>
  );
}

const CUSTOM_STYLE_SENTINEL = "__custom__";

function HaircutStyleEditor({ haircut, onSave }) {
  const predefined = useMemo(() => Object.values(CATEGORY_STYLES).flat(), []);
  const isCurrentCustom = haircut.style && !predefined.includes(haircut.style);
  const [mode, setMode] = useState(isCurrentCustom ? CUSTOM_STYLE_SENTINEL : (haircut.style || predefined[0]));
  const [customText, setCustomText] = useState(isCurrentCustom ? haircut.style : "");
  const [category, setCategory] = useState(haircut.category || PRIMARY_CATEGORIES[0]);
  const showingCustom = mode === CUSTOM_STYLE_SENTINEL;

  const commitPredefined = (value) => {
    setMode(value);
    if (value !== CUSTOM_STYLE_SENTINEL) {
      onSave({ style: value });
    }
  };
  const commitCustom = () => {
    const trimmed = customText.trim();
    if (!trimmed) return;
    onSave({ style: trimmed, category });
  };

  return (
    <div className="mt-2 space-y-2">
      <select
        value={mode}
        onChange={(e) => commitPredefined(e.target.value)}
        data-testid={`p-edit-style-${haircut.code}`}
        className="w-full text-xs border border-neutral-200 px-2 py-1 bg-white"
      >
        {Object.entries(CATEGORY_STYLES).map(([cat, styles]) => (
          <optgroup key={cat} label={cat}>
            {styles.map((s) => <option key={s} value={s}>{s}</option>)}
          </optgroup>
        ))}
        <option value={CUSTOM_STYLE_SENTINEL}>Diğer / Özel Model…</option>
      </select>
      {showingCustom && (
        <div className="flex gap-1">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid={`p-edit-custom-cat-${haircut.code}`}
            className="text-[11px] border border-neutral-200 px-1.5 py-1 bg-white w-24"
          >
            {PRIMARY_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onBlur={commitCustom}
            onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
            placeholder="Özel model adı"
            data-testid={`p-edit-custom-style-${haircut.code}`}
            className="flex-1 text-xs border border-neutral-900 px-2 py-1 bg-white outline-none"
          />
        </div>
      )}
    </div>
  );
}

function BarberPortfolioTab({ bid, haircuts, reload }) {
  const [form, setForm] = useState({
    title: "",
    category: PRIMARY_CATEGORIES[0],
    styleChoice: CATEGORY_STYLES[PRIMARY_CATEGORIES[0]][0],
    customStyle: "",
    description: "",
    image_url: "",
    featured: false,
  });
  const [imageDrafts, setImageDrafts] = useState({});
  const [savingPortfolio, setSavingPortfolio] = useState(false);

  useEffect(() => {
    setImageDrafts((prev) => {
      const next = { ...prev };
      haircuts.forEach((h) => {
        if (!(h.id in next)) {
          next[h.id] = h.image_url || "";
        }
      });
      return next;
    });
  }, [haircuts]);

  const setCategory = (cat) => setForm({ ...form, category: cat, styleChoice: CATEGORY_STYLES[cat][0], customStyle: "" });
  const isCustom = form.styleChoice === CUSTOM_STYLE_SENTINEL;

  const setHaircutImage = (id, value) => {
    setImageDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const savePortfolio = async () => {
    const changed = haircuts.filter((h) => {
      const draft = imageDrafts[h.id] ?? h.image_url ?? "";
      return draft !== (h.image_url ?? "");
    });

    if (changed.some((h) => !(imageDrafts[h.id] || "").trim())) {
      toast.error("Tıraş görseli boş bırakılamaz");
      return;
    }

    if (changed.length === 0) {
      toast.success("Kaydedilecek değişiklik yok");
      return;
    }

    setSavingPortfolio(true);

    try {
      await Promise.all(
        changed.map((h) =>
          api.patch(`/admin/haircuts/${h.id}`, {
            image_url: imageDrafts[h.id],
          })
        )
      );

      await reload();
      toast.success("Portfolyo kaydedildi");
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) ||
        "Portfolyo kaydedilemedi"
      );
    } finally {
      setSavingPortfolio(false);
    }
  };
  const add = async () => {
    if (!form.title || !form.image_url) { toast.error("Başlık ve görsel gerekli"); return; }
    const style = isCustom ? form.customStyle.trim() : form.styleChoice;
    if (isCustom && !style) { toast.error("Özel model adı gerekli"); return; }
    try {
      await api.post(`/admin/barbers/${bid}/haircuts`, {
        title: form.title,
        style,
        category: form.category,
        description: form.description,
        image_url: form.image_url,
        featured: form.featured,
      });
      setForm({ ...form, title: "", description: "", image_url: "", featured: false, customStyle: "" });
      reload();
      toast.success("Eklendi");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Eklenemedi"); }
  };
  const patch = async (id, updates) => {
    try { await api.patch(`/admin/haircuts/${id}`, updates); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Güncellenemedi"); }
  };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/admin/haircuts/${id}`); reload(); };

  return (
    <div>
      <div className="bg-white border border-neutral-200 p-6 mb-6">
        <div className="text-sm font-medium mb-4">Yeni model ekle</div>
        <div className="grid md:grid-cols-2 gap-4">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Başlık" data-testid="p-title" className="px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.category} onChange={(e) => setCategory(e.target.value)} data-testid="p-category" className="px-4 py-3 border border-neutral-200 bg-white">
              {PRIMARY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select value={form.styleChoice} onChange={(e) => setForm({ ...form, styleChoice: e.target.value })} data-testid="p-style" className="px-4 py-3 border border-neutral-200 bg-white">
              {CATEGORY_STYLES[form.category].map((s) => <option key={s} value={s}>{s}</option>)}
              <option value={CUSTOM_STYLE_SENTINEL}>Diğer / Özel Model…</option>
            </select>
          </div>
          {isCustom && (
            <input
              value={form.customStyle}
              onChange={(e) => setForm({ ...form, customStyle: e.target.value })}
              placeholder="Özel model adı (örn. Drop Fade, Wolf Cut)"
              data-testid="p-custom-style"
              className="md:col-span-2 px-4 py-3 border border-neutral-900 focus:outline-none bg-white"
            />
          )}
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Kısa açıklama (opsiyonel)" data-testid="p-desc" className="md:col-span-2 px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <div className="md:col-span-2">
            <ImageInput label="Görsel" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} scope={`haircut-${bid}`} testid="p-img" />
          </div>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="p-featured" />
            Öne çıkarılsın
          </label>
        </div>
        <button onClick={add} data-testid="p-add" className="mt-4 bg-neutral-900 text-white px-5 py-2.5 hover:bg-neutral-800 transition-colors flex items-center gap-2">
          <Plus size={16} /> Ekle
        </button>
      </div>

      <div className="flex justify-end mb-4">
        <button
          onClick={savePortfolio}
          disabled={savingPortfolio}
          data-testid="p-save"
          className="bg-neutral-900 text-white px-5 py-2.5 hover:bg-neutral-800 disabled:opacity-50 transition-colors flex items-center gap-2"
        >
          <Save size={15} />
          {savingPortfolio ? "Kaydediliyor…" : "Portfolyoyu Kaydet"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="p-grid">
        {haircuts.map((h) => (
          <div key={h.id} className={`bg-white border border-neutral-200 ${h.hidden ? "opacity-60" : ""}`} data-testid={`p-item-${h.code}`}>
            <div className="ar-3-4 overflow-hidden bg-neutral-100 relative">
              <img src={resolveImg(imageDrafts[h.id] ?? h.image_url)} alt={h.title} className="w-full h-full object-cover" />
              {h.featured && <div className="absolute top-2 left-2 bg-white text-[10px] tracking-widest px-2 py-0.5">ÖNE ÇIKAN</div>}
              {h.hidden && <div className="absolute top-2 right-2 bg-white text-[10px] tracking-widest px-2 py-0.5">GİZLİ</div>}
            </div>
            <div className="p-3">
              <div className="text-[10px] tracking-widest text-neutral-500">{h.code} · {h.category}</div>
              <input defaultValue={h.title} onBlur={(e) => patch(h.id, { title: e.target.value })} className="w-full text-sm font-medium border-b border-transparent focus:border-neutral-300 outline-none py-1" />
              <HaircutStyleEditor haircut={h} onSave={(updates) => patch(h.id, updates)} />

              <div className="mt-3">
                <ImageInput
                  label="Görsel"
                  value={imageDrafts[h.id] ?? h.image_url}
                  onChange={(value) => setHaircutImage(h.id, value)}
                  scope={`haircut-${bid}`}
                  testid={`p-edit-img-${h.code}`}
                  showPreview={false}
                />
              </div>

              <div className="mt-2 flex gap-1">
                <button onClick={() => patch(h.id, { featured: !h.featured })} className="flex-1 text-[11px] text-neutral-700 border border-neutral-200 hover:bg-neutral-50 py-1.5" data-testid={`p-feat-${h.code}`}>
                  {h.featured ? "Öne çıkarma" : "Öne çıkar"}
                </button>
                <button onClick={() => patch(h.id, { hidden: !h.hidden })} className="text-[11px] text-neutral-700 border border-neutral-200 hover:bg-neutral-50 px-2" data-testid={`p-hide-${h.code}`}>
                  {h.hidden ? <Eye size={13}/> : <EyeOff size={13}/>}
                </button>
                <button onClick={() => del(h.id)} className="text-[11px] text-red-600 border border-red-200 hover:bg-red-50 px-2" data-testid={`p-del-${h.code}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarberServicesTab({ bid, services, reload }) {
  const [form, setForm] = useState({ name: "", price: "" });
  const add = async () => {
    if (!form.name || !form.price) return;
    await api.post(`/admin/barbers/${bid}/services`, { name: form.name, price: Number(form.price) });
    setForm({ name: "", price: "" });
    reload();
  };
  const patch = async (id, k, v) => { await api.patch(`/admin/services/${id}`, { [k]: k === "price" ? Number(v) : v }); reload(); };
  const del = async (id) => { await api.delete(`/admin/services/${id}`); reload(); };
  return (
    <div>
      <div className="bg-white border border-neutral-200 p-6 mb-6">
        <div className="grid md:grid-cols-3 gap-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Hizmet adı" data-testid="s-name" className="px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Fiyat (₺)" type="number" data-testid="s-price" className="px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <button onClick={add} data-testid="s-add" className="bg-neutral-900 text-white px-5 py-2.5 hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2">
            <Plus size={16} /> Ekle
          </button>
        </div>
      </div>
      <div className="bg-white border border-neutral-200 divide-y divide-neutral-200" data-testid="s-list">
        {services.map((s) => (
          <div key={s.id} className={`grid grid-cols-12 items-center gap-3 px-4 py-3 ${s.hidden ? "opacity-60" : ""}`} data-testid={`s-row-${s.id}`}>
            <input defaultValue={s.name} onBlur={(e) => patch(s.id, "name", e.target.value)} className="col-span-6 border-b border-transparent focus:border-neutral-300 outline-none py-1" />
            <input defaultValue={s.price} type="number" onBlur={(e) => patch(s.id, "price", e.target.value)} className="col-span-2 border-b border-transparent focus:border-neutral-300 outline-none py-1 text-right" />
            <div className="col-span-1 text-neutral-500 text-sm">₺</div>
            <button onClick={() => patch(s.id, "hidden", !s.hidden)} className="col-span-1 text-xs text-neutral-600 hover:text-black flex items-center justify-center gap-1" data-testid={`s-hide-${s.id}`}>{s.hidden ? <Eye size={13}/> : <EyeOff size={13}/>}</button>
            <button onClick={() => del(s.id)} className="col-span-2 text-red-600 text-xs hover:bg-red-50 py-1 flex items-center justify-center gap-1" data-testid={`s-del-${s.id}`}>
              <Trash2 size={12} /> Sil
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarberReviewsTab({ bid, reviews, reload }) {
  const [form, setForm] = useState({ customer_name: "", rating: 5, text: "" });
  const add = async () => {
    if (!form.customer_name || !form.text) return;
    await api.post(`/admin/barbers/${bid}/reviews`, form);
    setForm({ customer_name: "", rating: 5, text: "" });
    reload();
  };
  const toggleHide = async (r) => { await api.patch(`/admin/reviews/${r.id}`, { hidden: !r.hidden }); reload(); };
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); reload(); };
  return (
    <div>
      <div className="bg-white border border-neutral-200 p-6 mb-6">
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Müşteri adı" data-testid="r-name" className="px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
          <select value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} data-testid="r-rating" className="px-4 py-3 border border-neutral-200 bg-white">
            {[5,4,3,2,1].map((n) => <option key={n} value={n}>{n} yıldız</option>)}
          </select>
          <button onClick={add} data-testid="r-add" className="bg-neutral-900 text-white px-5 py-2.5 hover:bg-neutral-800 transition-colors">Ekle</button>
        </div>
        <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Yorum" rows={3} data-testid="r-text" className="w-full px-4 py-3 border border-neutral-200 focus:outline-none focus:border-neutral-900" />
      </div>
      <div className="space-y-3" data-testid="r-list">
        {reviews.map((r) => (
          <div key={r.id} className={`bg-white border border-neutral-200 p-5 ${r.hidden ? "opacity-60" : ""}`} data-testid={`r-item-${r.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium">{r.customer_name} · {r.rating}★</div>
              <div className="flex items-center gap-3">
                <button onClick={() => toggleHide(r)} className="text-xs text-neutral-600 flex items-center gap-1 hover:text-black" data-testid={`r-toggle-${r.id}`}>
                  {r.hidden ? <><Eye size={13}/> Göster</> : <><EyeOff size={13}/> Gizle</>}
                </button>
                <button onClick={() => del(r.id)} className="text-xs text-red-600 flex items-center gap-1" data-testid={`r-del-${r.id}`}>
                  <Trash2 size={13} /> Sil
                </button>
              </div>
            </div>
            <p className="text-sm text-neutral-700">{r.text}</p>
            <div className="mt-2 text-xs text-neutral-400">{new Date(r.date).toLocaleDateString("tr-TR")}{r.hidden && " · Gizli"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarberQRTab({ slug }) {
  const url = `${window.location.origin}/${slug}?ref=qr`;
  const download = () => {
    const svg = document.getElementById("admin-qr-svg");
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: "image/svg+xml" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${slug}-qr.svg`;
    link.click();
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white border border-neutral-200 p-6">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">QR kodu</div>
        <div className="flex items-center justify-center p-6" data-testid="qr-code">
          <QRCodeSVG id="admin-qr-svg" value={url} size={240} bgColor="#FFFFFF" fgColor="#111111" level="M" />
        </div>
        <div className="mt-4 text-xs text-neutral-500 break-all" data-testid="qr-url">{url}</div>
        <button onClick={download} data-testid="qr-download" className="mt-4 bg-neutral-900 text-white px-5 py-2.5 hover:bg-neutral-800 transition-colors">SVG olarak indir</button>
      </div>
      <div className="bg-[#111111] text-white p-8 flex flex-col items-center justify-center" data-testid="qr-print">
        <div className="text-[10px] tracking-[0.32em] text-[#C4A661] mb-6">{slug.toUpperCase()}</div>
        <div className="font-editorial text-2xl md:text-3xl text-center leading-snug">Saç modelinde kararsız mısınız?</div>
        <div className="text-neutral-400 text-sm mt-2 mb-8">Çalışmalarımızı inceleyin.</div>
        <div className="p-4 bg-white">
          <QRCodeSVG value={url} size={180} bgColor="#FFFFFF" fgColor="#111111" level="M" />
        </div>
        <div className="mt-8 text-xs tracking-widest text-neutral-500">Kamerayı QR'a doğrultun</div>
      </div>
    </div>
  );
}
